import React, { createContext, FC, useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';

import UseLocalStorage from '../../uses/useLocalStorage';

import { ILangContext, ILangItem, ILangProvider } from './interfaces';

import { langs } from './langs';

// lang context
const LangContext = createContext({} as ILangContext);

// sync URL language to localStorage before hooks read it
// ensures shared URLs like /en/... load in the correct language
const syncUrlLangToStorage = () => {
  const pathLang = window.location.pathname.split('/')[1];
  if (pathLang) {
    const urlLangItem = langs.find(({ name }: ILangItem) => name === pathLang);
    if (urlLangItem) {
      window.localStorage.setItem('novonor_lang', JSON.stringify(urlLangItem));
    }
  }
};

// lang provider
const LangProvider: FC<ILangProvider> = ({ children }) => {
  // sync before UseLocalStorage reads the value
  syncUrlLangToStorage();

  let history = useHistory();
  const { i18n } = useTranslation();

  const [ lang, setLang ] = UseLocalStorage<any>('novonor_lang', {
    lang: 'pt',
    name: 'pt',
  });

  // set language
  const setLanguage = useCallback((value: string) => {
    if (value === lang.name) return false;
    
    const valueLang = langs.find(({ name }: ILangItem) => name === value);

    if (valueLang instanceof Object) {
      setLang(valueLang);
      history.push('/');
    }
  }, [ setLang, lang, history ]);

  // get lang
  const getLanguage = useCallback((lang: string) =>
    langs.filter((item: any) => item?.lang === lang)[0], []);

  // use effect
  useEffect(() => {
    i18n.changeLanguage(lang?.lang);
  }, [ lang, i18n ]);

  // render
  return (
    <LangContext.Provider value={{
      lang,
      items: langs,
      setLang: setLanguage,
      getLang: getLanguage,
    }}>
      {children}
    </LangContext.Provider>
  );
};

export { LangContext, LangProvider };
export default LangProvider;